import React, { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Sparkles, DollarSign, TrendingUp, TrendingDown, Target,
  Copy, CheckCircle, MessageSquare, Phone, ArrowRight, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadNegotiator = () => {
  const [formData, setFormData] = useState({
    origin_city: '',
    origin_state: '',
    destination_city: '',
    destination_state: '',
    equipment_type: 'flatbed',
    weight_lbs: '',
    broker_rate: '',
    broker_name: '',
    is_dedicated: false
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedScript, setCopiedScript] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.origin_state || !formData.destination_state || !formData.broker_rate) {
      toast.error('Please fill in origin state, destination state, and broker rate');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        broker_rate: parseFloat(formData.broker_rate),
        weight_lbs: formData.weight_lbs ? parseInt(formData.weight_lbs) : null
      };
      
      const res = await axios.post(`${API}/ai/negotiate`, payload);
      setResult(res.data);
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to analyze. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, scriptName) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(scriptName);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedScript(''), 2000);
  };

  const getVerdictColor = (verdict) => {
    switch(verdict) {
      case 'EXCELLENT': return 'bg-green-500';
      case 'FAIR': return 'bg-blue-500';
      case 'BELOW MARKET': return 'bg-yellow-500';
      case 'LOW BALL': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="negotiator-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground flex items-center gap-3">
              <Sparkles className="w-10 h-10 text-yellow-500" />
              AI Load Negotiator
            </h1>
            <p className="text-muted-foreground mt-2">Get instant market analysis and counter-offer suggestions</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-xl font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Load Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Origin */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Origin City</Label>
                    <Input
                      value={formData.origin_city}
                      onChange={(e) => setFormData({...formData, origin_city: e.target.value})}
                      placeholder="Los Angeles"
                    />
                  </div>
                  <div>
                    <Label>Origin State *</Label>
                    <Input
                      value={formData.origin_state}
                      onChange={(e) => setFormData({...formData, origin_state: e.target.value.toUpperCase()})}
                      placeholder="CA"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>

                {/* Destination */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Destination City</Label>
                    <Input
                      value={formData.destination_city}
                      onChange={(e) => setFormData({...formData, destination_city: e.target.value})}
                      placeholder="Dallas"
                    />
                  </div>
                  <div>
                    <Label>Destination State *</Label>
                    <Input
                      value={formData.destination_state}
                      onChange={(e) => setFormData({...formData, destination_state: e.target.value.toUpperCase()})}
                      placeholder="TX"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>

                {/* Equipment & Weight */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Equipment Type</Label>
                    <select
                      value={formData.equipment_type}
                      onChange={(e) => setFormData({...formData, equipment_type: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground"
                    >
                      <option value="sprinter_van">Sprinter Van</option>
                      <option value="cargo_van">Cargo Van</option>
                      <option value="box_truck">Box Truck</option>
                      <option value="hotshot">Hotshot</option>
                      <option value="dry_van">Dry Van</option>
                      <option value="reefer">Reefer</option>
                      <option value="flatbed">Flatbed</option>
                      <option value="step_deck">Step Deck</option>
                      <option value="conestoga">Conestoga</option>
                      <option value="lowboy">Lowboy</option>
                      <option value="rgn">RGN</option>
                      <option value="double_drop">Double Drop</option>
                      <option value="curtain_side">Curtain Side</option>
                      <option value="tanker">Tanker</option>
                      <option value="hopper">Hopper</option>
                      <option value="dump_truck">Dump Truck</option>
                      <option value="power_only">Power Only</option>
                    </select>
                  </div>
                  <div>
                    <Label>Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={formData.weight_lbs}
                      onChange={(e) => setFormData({...formData, weight_lbs: e.target.value})}
                      placeholder="42000"
                    />
                  </div>
                </div>

                {/* Broker Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Broker's Offered Rate *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={formData.broker_rate}
                        onChange={(e) => setFormData({...formData, broker_rate: e.target.value})}
                        placeholder="4500"
                        className="pl-9"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Broker Name (optional)</Label>
                    <Input
                      value={formData.broker_name}
                      onChange={(e) => setFormData({...formData, broker_name: e.target.value})}
                      placeholder="John from XYZ Logistics"
                    />
                  </div>
                </div>

                {/* Dedicated Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="dedicated"
                    checked={formData.is_dedicated}
                    onChange={(e) => setFormData({...formData, is_dedicated: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="dedicated" className="cursor-pointer">This is dedicated/weekly freight</Label>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      Analyzing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Analyze & Get Counter Offer
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {/* Verdict Card */}
              <Card className={`${getVerdictColor(result.analysis.verdict)} text-white`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">AI VERDICT</p>
                      <p className="text-3xl font-black">{result.analysis.verdict_emoji} {result.analysis.verdict}</p>
                      <p className="mt-2 text-sm opacity-90">{result.analysis.reasoning}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-90">RECOMMENDED</p>
                      <p className="text-xl font-bold">{result.analysis.recommended_action}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Market Analysis */}
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Market Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Lane</p>
                      <p className="text-lg font-bold text-white">{result.lane}</p>
                      <p className="text-xs text-slate-400">{result.market_data.distance_miles} miles</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Market Rate</p>
                      <p className="text-lg font-bold text-green-400">${result.market_data.market_rate.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">${result.market_data.market_rate_per_mile}/mile</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400">Broker Offered</p>
                        <p className="text-xl font-bold text-white">${result.market_data.broker_offered.toLocaleString()}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-500" />
                      <div className="text-right">
                        <p className="text-xs text-slate-400">vs Market</p>
                        <p className={`text-xl font-bold ${result.market_data.difference_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {result.market_data.difference_percentage >= 0 ? '+' : ''}{result.market_data.difference_percentage}%
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Counter Offers */}
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-yellow-500" />
                    Suggested Counter Offers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-slate-800 rounded-lg text-center border-2 border-transparent hover:border-blue-500 transition-colors">
                      <p className="text-xs text-slate-400 mb-1">CONSERVATIVE</p>
                      <p className="text-xl font-bold text-white">${result.counter_offers.conservative.rate.toLocaleString()}</p>
                      <p className="text-xs text-green-400">Your profit: ${result.counter_offers.conservative.your_profit.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-blue-600/20 rounded-lg text-center border-2 border-blue-500">
                      <p className="text-xs text-blue-400 mb-1">RECOMMENDED</p>
                      <p className="text-xl font-bold text-white">${result.counter_offers.fair.rate.toLocaleString()}</p>
                      <p className="text-xs text-green-400">Your profit: ${result.counter_offers.fair.your_profit.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg text-center border-2 border-transparent hover:border-yellow-500 transition-colors">
                      <p className="text-xs text-yellow-400 mb-1">PREMIUM</p>
                      <p className="text-xl font-bold text-white">${result.counter_offers.premium.rate.toLocaleString()}</p>
                      <p className="text-xs text-green-400">Your profit: ${result.counter_offers.premium.your_profit.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {result.your_profit.potential_gain > 0 && (
                    <div className="mt-3 p-3 bg-green-500/20 rounded-lg border border-green-500/50 text-center">
                      <p className="text-green-400 font-bold">
                        💰 Negotiating to fair rate = +${result.your_profit.potential_gain.toLocaleString()} extra profit!
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Scripts & Talking Points */}
        {result && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Scripts */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  Ready-to-Send Scripts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(result.scripts).map(([key, script]) => (
                  <div key={key} className="p-4 bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-slate-300 capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(script, key)}
                        className="text-xs"
                      >
                        {copiedScript === key ? (
                          <><CheckCircle className="w-3 h-3 mr-1 text-green-500" /> Copied!</>
                        ) : (
                          <><Copy className="w-3 h-3 mr-1" /> Copy</>
                        )}
                      </Button>
                    </div>
                    <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans overflow-x-auto max-h-40">
                      {script}
                    </pre>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Phone Talking Points */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-500" />
                  Phone Negotiation Talking Points
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {result.talking_points.map((point, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800 rounded-lg">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-xs font-bold text-white">
                        {idx + 1}
                      </span>
                      <p className="text-sm text-slate-300">{point}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-4 bg-yellow-500/20 rounded-lg border border-yellow-500/50">
                  <p className="text-sm text-yellow-400 font-bold mb-2">💡 Pro Tip</p>
                  <p className="text-xs text-yellow-300">
                    Always let them name a number first. If they push back on your counter, ask "What rate would work for you?" - often they'll come up from their original offer.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LoadNegotiator;
